import React, { useState } from 'react';
import {
  HelpCircle, BookOpen, Shield, Lock, CreditCard,
  Server, ExternalLink, Copy, Check, Search, Terminal,
  Code2
} from 'lucide-react';

export const HelpDocsPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<'all' | 'arch' | 'crypto' | 'kyc' | 'payments' | 'api' | 'runbooks'>('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const ARTICLES = [
    {
      id: 'doc-arch-1',
      category: 'arch',
      icon: Server,
      title: 'Edge-Native System Architecture',
      subtitle: 'Cloudflare Workers + D1 + Durable Objects + R2',
      summary: 'Amoura operates on a 100% serverless edge stack powered by Cloudflare V8 isolates with zero traditional virtual machines.',
      content: [
        'Compute: Hono running on Cloudflare Workers with < 15ms cold start times across global POPs.',
        'Primary Database: Cloudflare D1 (serverless SQLite at the edge) for users, matches, and KYC records.',
        'Real-time State: Cloudflare Durable Objects coordinate stateful WebSockets for encrypted chat rooms and mutual matching.',
        'Object Storage: Cloudflare R2 stores profile photos and video selfies with zero egress charges.',
        'Session Cache: Cloudflare KV stores user identity prekeys (ECDH) and active chat session timers.',
      ],
      codeSnippet: `// Worker Entrypoint (src/index.ts)
const app = new OpenAPIHono<{ Bindings: Env }>();
app.get('/health', (c) => c.text('Dating App Edge API is online and healthy.'));
export default app;`,
    },
    {
      id: 'doc-crypto-1',
      category: 'crypto',
      icon: Lock,
      title: 'Signal Protocol & E2EE Chat Security',
      subtitle: 'ECDH P-256 + AES-256-GCM Zero-Knowledge Encryption',
      summary: 'All direct messages exchanged between matched users are encrypted client-side. The Cloudflare Workers edge routes encrypted envelopes without ability to decrypt.',
      content: [
        'Key Exchange: Elliptic Curve Diffie-Hellman (ECDH) over NIST P-256 curve.',
        'Key Publishing: Mobile clients generate Identity and Prekey pairs upon onboarding and publish public keys via /api/prekeys.',
        'Payload Security: AES-256-GCM symmetric encryption with a unique initialization vector (IV) per message.',
        'Zero Knowledge: The server only sees { ciphertext, iv, recipientId }. Plaintext is never stored in D1 or logged.',
      ],
      codeSnippet: `// Client-Side Encryption
const iv = crypto.getRandomValues(new Uint8Array(12));
const ciphertext = await crypto.subtle.encrypt(
  { name: 'AES-GCM', iv },
  derivedSharedKey,
  new TextEncoder().encode(messageText)
);`,
    },
    {
      id: 'doc-kyc-1',
      category: 'kyc',
      icon: Shield,
      title: 'Identity Verification & Trust Score Engine',
      subtitle: 'Dojah NIN/BVN validation and selfie liveness detection',
      summary: 'The Trust Score (0-100) eliminates bots and romance scams before users can engage in discovery.',
      content: [
        'Discovery Gate: Users must upload at least 3 approved photos/videos before appearing in discovery swiping.',
        'Phone OTP (+15 pts): Verified via Termii SMS gateway during registration.',
        'NIN / BVN (+30 pts): Verified via Dojah identity API against government registries.',
        'Selfie Liveness (+25 pts): Biometric facial mesh match against uploaded identity documents.',
        'Badge Tiers: Unverified (0-30, hidden), Basic (31-55 🟡), Trusted (56-80 🟢), Elite (81-100 💎).',
      ],
      codeSnippet: `curl -X POST http://127.0.0.1:8787/kyc/verify-nin \\
  -H "Authorization: Bearer <JWT_TOKEN>" \\
  -H "Content-Type: application/json" \\
  -d '{"nin": "12345678901"}'`,
    },
    {
      id: 'doc-payments-1',
      category: 'payments',
      icon: CreditCard,
      title: 'SquadCo Payment Processing & Webhooks',
      subtitle: 'Naira pricing tiers, cards, bank transfer & USSD',
      summary: 'Subscriptions are billed in Nigerian Naira (₦) through SquadCo gateway with automated signature-verified webhook provisioning.',
      content: [
        'Instant Match Ticket (₦500): Single-use mutual matching pass.',
        '2-Hour Chat Session (₦500): Unlocks continuous encrypted chat for exactly 120 minutes with a chosen match.',
        'Elite Weekly Pass (₦5,000): Unlimited chatting, priority discovery ranking, and trust score boost for 7 days.',
        'Webhook Verification: All webhooks to /subscriptions/webhook are verified using HMAC-SHA512 with SQUAD_WEBHOOK_SECRET.',
      ],
      codeSnippet: `// Webhook Signature Verification
const computedHmac = crypto.subtle.sign('HMAC', key, rawBody);
if (headerSignature !== computedHmacHex) {
  return c.text('Invalid SquadCo signature', 401);
}`,
    },
    {
      id: 'doc-api-1',
      category: 'api',
      icon: Code2,
      title: 'Edge API Endpoints & Swagger Documentation',
      subtitle: 'OpenAPI 3.1 specification hosted on Cloudflare Workers',
      summary: 'The Edge API adheres to strict OpenAPI 3.1 schema validation via @hono/zod-openapi.',
      content: [
        'Swagger UI: Interactive API documentation is available locally at http://127.0.0.1:8787/docs',
        'OpenAPI Spec: Raw JSON specification available at /openapi.json',
        'Authentication: REST routes require Authorization: Bearer <JWT>. WebSocket routes accept ?token=<JWT>.',
        'Admin Endpoints: Management routes mounted under /api/admin/* with zero-trust protection.',
      ],
      codeSnippet: `curl http://127.0.0.1:8787/api/admin/stats \\
  -H "Authorization: Bearer <ADMIN_JWT>"`,
    },
    {
      id: 'doc-runbooks-1',
      category: 'runbooks',
      icon: Terminal,
      title: 'Operations Runbook: Dispute Resolution & KYC Bypass',
      subtitle: 'Standard Operating Procedures for Backoffice Administrators',
      summary: 'Action guides for resolving payment disputes, approving flagged profiles, and data migrations.',
      content: [
        'Resolving Disputes: Navigate to Payments > Disputes. Inspect user debit reference. Click "Refund ₦500" to trigger Squad refund API or "Dismiss" if chat minutes were consumed.',
        'Manual KYC Approval: Open KYC Queue. Verify Dojah NIN match. Click "Verify ID" to automatically promote user trust score to 75 (Trusted).',
        'Toggling Sandbox Mode: Use the top header switcher to test simulated scenarios without affecting production D1 rows.',
        'Data Export: Use Dashboard > Export Sync to download timestamped JSON snapshots of current edge state.',
      ],
      codeSnippet: `# Wrangler D1 manual database inspection
npx wrangler d1 execute dating-app-db --local --command="SELECT COUNT(*) FROM users;"`,
    },
  ];

  const filteredArticles = ARTICLES.filter((art) => {
    const matchesCategory = activeCategory === 'all' || art.category === activeCategory;
    const matchesSearch =
      !search ||
      art.title.toLowerCase().includes(search.toLowerCase()) ||
      art.summary.toLowerCase().includes(search.toLowerCase()) ||
      art.content.some((c) => c.toLowerCase().includes(search.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* ── Page Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-white font-['Outfit']">Help & Documentation</h1>
            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30 flex items-center gap-1.5 shadow-sm">
              <BookOpen size={12} />
              Knowledge Hub
            </span>
          </div>
          <p className="text-sm text-zinc-400 mt-1">
            Technical architecture, E2EE cryptographic protocols, KYC verification algorithms & ops runbooks
          </p>
        </div>

        {/* Live Swagger UI Link */}
        <a
          href="http://127.0.0.1:8787/docs"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white shadow-lg shadow-purple-500/20 transition-all self-start sm:self-auto cursor-pointer"
        >
          <span>Open Swagger UI</span>
          <ExternalLink size={14} />
        </a>
      </div>

      {/* ── Search & Filter Bar ── */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            placeholder="Search guides, cURL commands, crypto specs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-xs rounded-xl bg-[#0d0d1a] border border-[#1e1e3a] text-white placeholder-zinc-500 focus:outline-none focus:border-pink-500"
          />
        </div>

        {/* Category Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs">
          {[
            { id: 'all', label: 'All Docs' },
            { id: 'arch', label: 'Architecture' },
            { id: 'crypto', label: 'E2EE Crypto' },
            { id: 'kyc', label: 'KYC & Trust' },
            { id: 'payments', label: 'SquadCo (₦)' },
            { id: 'api', label: 'API Specs' },
            { id: 'runbooks', label: 'Runbooks' },
          ].map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id as any)}
              className={`px-3 py-1.5 rounded-xl font-bold transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat.id
                  ? 'bg-pink-600 text-white shadow-sm'
                  : 'bg-[#0d0d1a] border border-[#1e1e3a] text-zinc-400 hover:text-white hover:border-zinc-600'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Articles List ── */}
      <div className="space-y-6">
        {filteredArticles.map((art) => (
          <div
            key={art.id}
            className="p-6 rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a] hover:border-zinc-700 transition-all space-y-4 shadow-lg"
          >
            {/* Article Header */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-pink-500/20 to-purple-500/20 border border-pink-500/30 flex items-center justify-center text-pink-400 shrink-0">
                  <art.icon size={20} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white font-['Outfit']">{art.title}</h3>
                  <p className="text-xs text-pink-400 font-medium">{art.subtitle}</p>
                </div>
              </div>
            </div>

            <p className="text-xs text-zinc-300 leading-relaxed">{art.summary}</p>

            {/* Bullet points */}
            <ul className="space-y-1.5 text-xs text-zinc-400 list-disc list-inside">
              {art.content.map((point, idx) => (
                <li key={idx} className="leading-relaxed">
                  <span className="text-zinc-300">{point}</span>
                </li>
              ))}
            </ul>

            {/* Code Snippet */}
            {art.codeSnippet && (
              <div className="rounded-xl bg-[#070710] border border-[#1e1e3a] overflow-hidden">
                <div className="flex items-center justify-between px-3.5 py-2 bg-[#141428] border-b border-[#1e1e3a] text-[11px] text-zinc-400">
                  <span className="font-mono flex items-center gap-1.5">
                    <Terminal size={12} className="text-pink-400" /> Reference Snippet
                  </span>
                  <button
                    onClick={() => handleCopy(art.id, art.codeSnippet!)}
                    className="flex items-center gap-1 text-zinc-400 hover:text-white transition-colors cursor-pointer"
                  >
                    {copiedId === art.id ? (
                      <>
                        <Check size={12} className="text-emerald-400" />
                        <span className="text-emerald-400">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy size={12} />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>
                <pre className="p-3.5 text-xs font-mono text-zinc-300 overflow-x-auto">
                  <code>{art.codeSnippet}</code>
                </pre>
              </div>
            )}
          </div>
        ))}

        {filteredArticles.length === 0 && (
          <div className="p-12 text-center rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a] text-zinc-400">
            <HelpCircle size={32} className="mx-auto text-zinc-600 mb-3" />
            <p className="text-sm font-bold text-white">No matching documentation found</p>
            <p className="text-xs text-zinc-500 mt-1">Try searching for keywords like "D1", "ECDH", "NIN", or "SquadCo"</p>
          </div>
        )}
      </div>
    </div>
  );
};
